import { Image } from 'expo-image'
import { View } from 'react-native'
import Animated, {
  interpolate,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated'

import { Typography } from '@/src/elements/text/Typography'
import { useTimeout } from '@/src/general/hooks/useTimeout'
import { useTranslate } from '@/src/lang/useTranslate'

const cards = [
  { key: 1156, column: 0, row: 0, image: require('@/img/onboarding/discovery-card-1156.webp') },
  { key: 1160, column: 0, row: 1, image: require('@/img/onboarding/discovery-card-1160.webp') },
  { key: 1163, column: 0, row: 2, image: require('@/img/onboarding/journey-thumbnail-2.webp') },
  { key: 1166, column: 0, row: 3, image: require('@/img/onboarding/frame-16068.webp') },
  { key: 1158, column: 1, row: 0, image: require('@/img/onboarding/journey-thumbnail-2.webp') },
  { key: 1157, column: 1, row: 1, image: require('@/img/onboarding/discovery-card-1157.webp') },
  { key: 1161, column: 1, row: 2, image: require('@/img/onboarding/discovery-card-1161.webp') },
  { key: 1164, column: 1, row: 3, image: require('@/img/onboarding/discovery-card-1164.webp') },
  { key: 1167, column: 1, row: 4, image: require('@/img/onboarding/journey-thumbnail-3.webp') },
  { key: 1159, column: 2, row: 0, image: require('@/img/onboarding/discovery-card-1159.webp') },
  { key: 1162, column: 2, row: 1, image: require('@/img/onboarding/discovery-card-1162.webp') },
  { key: 1165, column: 2, row: 2, image: require('@/img/onboarding/discovery-card-1165.webp') },
  { key: 1168, column: 2, row: 3, image: require('@/img/onboarding/journey-thumbnail-1.webp') },
  { key: 1173, column: 3, row: 0, image: require('@/img/onboarding/discovery-card-1164.webp') },
  { key: 1169, column: 3, row: 1, image: require('@/img/onboarding/discovery-card-1160.webp') },
  { key: 1170, column: 3, row: 2, image: require('@/img/onboarding/journey-thumbnail-2.webp') },
  { key: 1171, column: 3, row: 3, image: require('@/img/onboarding/discovery-card-1156.webp') },
  { key: 1172, column: 3, row: 4, image: require('@/img/onboarding/discovery-card-1159.webp') },
  { key: 1174, column: 4, row: 0, image: require('@/img/onboarding/journey-thumbnail-1.webp') },
  { key: 1175, column: 4, row: 1, image: require('@/img/onboarding/discovery-card-1165.webp') },
  { key: 1176, column: 4, row: 2, image: require('@/img/onboarding/discovery-card-1161.webp') },
  { key: 1177, column: 4, row: 3, image: require('@/img/onboarding/frame-16066.webp') },
] as const

const CARD_WIDTH = 79
const CARD_HEIGHT = 123
const COLUMN_GAP = 15
const ROW_GAP = 18
const MOSAIC_LEFT = 60
const MOSAIC_TOP = -80

// Cards are laid out before the whole mosaic is rotated, so their spacing remains regular.
const COLUMN_STEP = CARD_WIDTH + COLUMN_GAP
const ROW_STEP = CARD_HEIGHT + ROW_GAP
const CARD_STAGGER_MS = 15

type TMosaicCardProps = Omit<(typeof cards)[number], 'key'> & { index: number }

const MosaicCard = ({ image, column, row, index }: TMosaicCardProps) => {
  const progress = useSharedValue(0)

  useTimeout(() => {
    progress.value = withDelay(
      50 + index * CARD_STAGGER_MS,
      withTiming(1, { duration: 700, reduceMotion: ReduceMotion.System }),
    )
  }, 0)

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 1], [0.35, 1]),
    transform: [
      { translateX: interpolate(progress.value, [0, 1], [40, 0]) },
      { translateY: interpolate(progress.value, [0, 1], [-150, 0]) },
    ],
  }))

  // Odd columns start half a row earlier to form the staggered Figma mosaic.
  const rowOffset = column % 2 === 0 ? 0 : -ROW_STEP / 2

  return (
    <Animated.View
      className="absolute rounded-2xl shadow-md"
      style={[
        {
          left: column * COLUMN_STEP,
          top: row * ROW_STEP + rowOffset,
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
        },
        animatedStyle,
      ]}
    >
      <Image source={image} contentFit="cover" className="h-full w-full rounded-2xl" />
    </Animated.View>
  )
}

export const StepFour = () => {
  const { t } = useTranslate()

  return (
    <>
      <View className="h-[300px] w-full overflow-hidden top-[-80px]">
        <View className="absolute inset-0" style={{ transform: [{ translateX: 80 }, { translateY: -100 }] }}>
          <View
            className="absolute"
            style={{
              left: MOSAIC_LEFT,
              top: MOSAIC_TOP,
              width: 5 * COLUMN_STEP,
              height: 5 * ROW_STEP,
              transformOrigin: [0, 0, 0],
              transform: [{ rotateX: '50deg' }, { rotateY: '-10deg' }, { rotateZ: '30deg' }],
            }}
          >
            {cards.map(({ key, ...card }, index) => (
              <MosaicCard key={key} {...card} index={index} />
            ))}
          </View>
        </View>
      </View>
      <Typography size="lg" black colors="primary" contrast="default" className="mx-6 -top-24">
        {t('prayer-space.onboarding.step-4.title')}
      </Typography>
      <Typography size="sm" colors="primary" contrast="high" className="mx-6 mt-4 -top-24">
        {t('prayer-space.onboarding.step-4.description')}
      </Typography>
    </>
  )
}
